import { useEffect, useState } from 'react'

import { SectionTypeEnum } from 'common/enums/AfterSales'
import { ITicket } from 'store/afterSales/types'
import { ListDescription } from 'components'
import { Icons } from 'utils/icons'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import { useHistory } from 'react-router-dom'
import useBoundState from 'store'

import { Flex } from '@interco/inter-ui/components/Flex'
import { Text } from '@interco/inter-ui/components/Text'
import { Loading } from '@interco/inter-ui/components/Loading'

import { PaymentProcessingBottomSheet } from './PaymentProcessingBottomSheet'
import * as S from './AvailableTicketsSection.style'

interface IProps {
  availableTickets: ITicket[]
  processingTickets?: number
  paymentProcessingTickets?: number
  analyticsFunction: () => void
}

export const AvailableTicketsSection = ({
  availableTickets,
  processingTickets,
  paymentProcessingTickets,
  analyticsFunction,
}: IProps) => {
  const history = useHistory()
  const contentCapitalization = useBoundState((state) => state.contentCapitalization)
  const [showPaymentProcessingBottomSheet, setShowPaymentProcessingBottomSheet] = useState(false)
  const hasProcessingTickets = Boolean(processingTickets && processingTickets > 0)
  const hasPaymentProcessingTickets = Boolean(
    paymentProcessingTickets && paymentProcessingTickets > 0,
  )

  useEffect(() => {
    analyticsFunction()
  })

  const handleOpenPaymentProcessingBottomSheet = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowPaymentProcessingBottomSheet(true)
  }

  const handleClosePaymentProcessingBottomSheet = () => {
    setShowPaymentProcessingBottomSheet(false)
  }

  const IconLeft = (
    <S.IconWrap $bgColor="var(--primary100)">
      <Icons.Gift color="var(--primary500)" width={24} height={24} />
    </S.IconWrap>
  )
  const chevronRight = <Icons.ChevronRight color="var(--primary500)" width={24} height={24} />

  const availableButtonTitle =
    availableTickets.length === 1
      ? `1 ${contentCapitalization.value1}`
      : `${availableTickets.length} ${contentCapitalization.value2}`
  const ticketListDescription = (
    <ListDescription
      margin="16px 0 0"
      iconLeft={IconLeft}
      iconRight={chevronRight}
      withBorder
      onClick={() => history.push(TypesRoutes.START)}
    >
      <Flex direction="column">
        <Text variant="body-2" colorWeight={500} bold>
          {availableButtonTitle}
        </Text>
      </Flex>
    </ListDescription>
  )

  const processingTicketListDescription = (
    <ListDescription
      margin="16px 0 0"
      iconLeft={
        <S.IconWrap $bgColor="var(--primary100)">
          <Loading width={24} height={24} />
        </S.IconWrap>
      }
      withBorder
    >
      <Flex direction="column">
        <Text variant="body-2" colorWeight={500} bold>
          {processingTickets === 1
            ? `1 ${contentCapitalization.value1}`
            : `${processingTickets} ${contentCapitalization.value2}`}
        </Text>
        <Text variant="caption-1">{SectionTypeEnum.PROCESSING}</Text>
      </Flex>
    </ListDescription>
  )

  const helpIcon = (
    <S.HelpIconButton
      type="button"
      onClick={handleOpenPaymentProcessingBottomSheet}
      data-testid="help-icon-button"
    >
      <Icons.Help color="var(--primary500)" width={24} height={24} />
    </S.HelpIconButton>
  )

  const paymentProcessingTicketListDescription = (
    <ListDescription
      margin="16px 0 0"
      iconLeft={
        <S.IconWrap $bgColor="var(--primary100)">
          <Loading width={24} height={24} />
        </S.IconWrap>
      }
      iconRight={helpIcon}
      withBorder
    >
      <Flex direction="column">
        <Text variant="body-2" colorWeight={500} bold>
          {paymentProcessingTickets === 1
            ? `1 ${contentCapitalization.value1}`
            : `${paymentProcessingTickets} ${contentCapitalization.value2}`}
        </Text>
        <Text variant="caption-1">{SectionTypeEnum.PAYMENT_PROCESSING}</Text>
      </Flex>
    </ListDescription>
  )

  if (availableTickets.length === 0 && !processingTickets && !paymentProcessingTickets) return <></>

  return (
    <>
      {availableTickets.length > 0 && ticketListDescription}
      {hasProcessingTickets && processingTicketListDescription}
      {hasPaymentProcessingTickets && paymentProcessingTicketListDescription}
      {showPaymentProcessingBottomSheet && (
        <PaymentProcessingBottomSheet
          handleCloseBottomSheet={handleClosePaymentProcessingBottomSheet}
        />
      )}
    </>
  )
}
